import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ChevronLeft,
  Loader2,
  Paperclip,
  Send,
  Ticket as TicketIcon,
  X,
  FileText,
  Eye,
  ExternalLink,
  ArrowRight,
  UserCog,
  History,
  Activity as ActivityIcon,
  AlertTriangle,
  Upload,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TICKET_TYPE_LABELS = {
  chamada_agendada: 'Novo Pedido de Chamada Agendada',
  chamada_imediata: 'Novo Pedido de Chamada Imediata',
  tratamento_pendencia: 'Tratamento Pendência',
  pedido_anulacao: 'Pedido Anulação',
};

const STATUSES = [
  { value: 'novo', label: 'Novo' },
  { value: 'em_tratamento', label: 'Em Tratamento' },
  { value: 'concluido', label: 'Concluído' },
];

const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

export const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingAssignee, setUpdatingAssignee] = useState(false);
  const [contractFiles, setContractFiles] = useState([]);

  const canManage = ['super_admin', 'gestor', 'admin', 'backoffice', 'parceiro'].includes(user?.role);
  const canViewAllDocs = ['super_admin', 'gestor', 'admin'].includes(user?.role);
  const canReplaceCorrupt = user?.role === 'super_admin' || user?.role === 'gestor' || (user?.role === 'admin' && (user?.permissions?.editar_estado_inline === true));

  const handleReplaceCorrupt = async (fileId, fileList) => {
    const replacement = fileList?.[0];
    if (!replacement) return;
    const formData = new FormData();
    formData.append('file', replacement);
    try {
      await axios.post(`${API_URL}/files/${fileId}/replace`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Ficheiro substituído com sucesso');
      // Reload contract files
      if (ticket?.contract_id) {
        const filesRes = await axios.get(`${API_URL}/files/contract/${ticket.contract_id}`);
        setContractFiles(filesRes.data || []);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao substituir ficheiro');
    }
  };

  const fetchTicket = useCallback(async () => {
    try {
      const [ticketRes, commentsRes, auditRes] = await Promise.all([
        axios.get(`${API_URL}/tickets/${id}`),
        axios.get(`${API_URL}/tickets/${id}/comments`),
        axios.get(`${API_URL}/tickets/${id}/audit`),
      ]);
      setTicket(ticketRes.data);
      setComments(commentsRes.data || []);
      setAudit(auditRes.data || []);
      // Fetch contract files if ticket is linked to a contract
      if (ticketRes.data?.contract_id) {
        try {
          const filesRes = await axios.get(`${API_URL}/files/contract/${ticketRes.data.contract_id}`);
          setContractFiles(filesRes.data || []);
        } catch {
          // non-fatal
        }
      }
      // Mark as read (silent)
      axios.post(`${API_URL}/tickets/${id}/mark-read`).catch(() => {});
    } catch (err) {
      toast.error('Erro ao carregar ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchUsers = useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await axios.get(`${API_URL}/users`);
      setUsers(res.data || []);
    } catch {
      // Non-fatal: keep reassign disabled
    }
  }, [canManage]);

  useEffect(() => { fetchTicket(); fetchUsers(); }, [fetchTicket, fetchUsers]);
  useRefreshListener(fetchTicket);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), "dd MMM yyyy 'às' HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API_URL}/tickets/${id}`, { status: newStatus });
      const wasAnulacao = ticket?.tipo === 'pedido_anulacao' && newStatus === 'concluido';
      toast.success(wasAnulacao ? 'Ticket concluído. Contrato anulado automaticamente.' : 'Estado atualizado');
      fetchTicket();
    } catch (err) {
      toast.error('Erro ao atualizar estado');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAssigneeChange = async (newAssigneeId) => {
    if (newAssigneeId === ticket.assigned_to) return;
    setUpdatingAssignee(true);
    try {
      await axios.put(`${API_URL}/tickets/${id}`, { assigned_to: newAssigneeId });
      toast.success('Ticket reatribuído');
      fetchTicket();
    } catch (err) {
      toast.error('Erro ao reatribuir');
    } finally {
      setUpdatingAssignee(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() && pendingFiles.length === 0) {
      toast.error('Escreva uma mensagem ou anexe um ficheiro');
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('content', reply);
      for (const f of pendingFiles) fd.append('files', f);
      // axios sets Content-Type with the multipart boundary automatically — do not override
      await axios.post(`${API_URL}/tickets/${id}/comments`, fd);
      toast.success(pendingFiles.length > 1 ? `${pendingFiles.length} ficheiros enviados` : 'Resposta enviada');
      setReply('');
      setPendingFiles([]);
      fetchTicket();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar resposta');
    } finally {
      setSending(false);
    }
  };

  const handleViewFile = (fileId) => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/files/${fileId}/download?auth=${token}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!ticket) return null;

  const tipoLabel = TICKET_TYPE_LABELS[ticket.tipo] || ticket.tipo;

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl" data-testid="ticket-detail-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const r = location.state?.returnTo;
              if (r) navigate(r);
              else if (window.history.length > 1) navigate(-1);
              else navigate('/tickets');
            }}
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <TicketIcon className="w-5 h-5 text-[#2D3E6F]" />
              <h1 className="text-2xl font-bold text-slate-900 font-outfit" data-testid="ticket-title">
                {tipoLabel}
              </h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Aberto por <span className="font-medium text-slate-700">{ticket.created_by_name || '-'}</span> em {formatDate(ticket.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={ticket.priority} />
            {canManage ? (
              <Select value={ticket.status} onValueChange={handleStatusChange} disabled={updatingStatus}>
                <SelectTrigger className="w-44" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="px-3 py-1.5" data-testid="status-readonly">
                <StatusBadge status={ticket.status} />
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column: description + thread */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Descrição</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {ticket.description ? (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="ticket-description">{ticket.description}</p>
                ) : (
                  <p className="text-sm text-slate-400">Sem descrição.</p>
                )}
                {ticket.motivo_anulacao && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs font-semibold text-red-700 mb-1">Motivo da Anulação</p>
                    <p className="text-sm text-red-800 whitespace-pre-wrap">{ticket.motivo_anulacao}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Thread */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Conversa ({comments.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-5" data-testid="comments-thread">
                {comments.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4 text-center">Sem respostas ainda. Seja o primeiro a responder.</p>
                ) : (
                  comments.map((c) => {
                    if (c.is_system) {
                      return (
                        <div
                          key={c.id}
                          className="flex justify-center"
                          data-testid={`comment-${c.id}`}
                        >
                          <div className="px-4 py-2 bg-slate-100 border border-slate-200 rounded-full text-xs text-slate-600 max-w-[90%]">
                            <span className="text-slate-400 mr-2">{formatDate(c.created_at)} ·</span>
                            <span className="font-medium text-slate-700">{c.content}</span>
                          </div>
                        </div>
                      );
                    }
                    return (
                    <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                      <Avatar className="h-9 w-9 flex-shrink-0 bg-[#2D3E6F]/10">
                        <AvatarFallback className="bg-[#2D3E6F] text-white text-xs font-semibold">
                          {initials(c.created_by_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-sm font-semibold text-slate-800">{c.created_by_name || 'Utilizador'}</span>
                          <span className="text-xs text-slate-400">{formatDate(c.created_at)}</span>
                        </div>
                        {c.content && (
                          <p className="text-sm text-slate-700 whitespace-pre-wrap mb-2">{c.content}</p>
                        )}
                        {c.file && (
                          <button
                            type="button"
                            onClick={() => handleViewFile(c.file.id)}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-sm transition-colors"
                            data-testid={`comment-file-${c.id}`}
                          >
                            <FileText className="w-4 h-4 text-[#2D3E6F]" />
                            <span className="font-medium text-slate-700 truncate max-w-[260px]">{c.file.filename}</span>
                            <span className="text-xs text-slate-400">{formatSize(c.file.size)}</span>
                            <Eye className="w-3.5 h-3.5 text-slate-400 ml-1" />
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })
                )}
              </CardContent>

              {/* Documentos do Contrato */}
              {ticket.contract_id && contractFiles.length > 0 && (
                <div className="border-t border-slate-100 px-6 py-5 bg-slate-50/40" data-testid="contract-docs-section">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#2D3E6F]" />
                      Documentos do Contrato
                      {!canViewAllDocs && contractFiles.length > 1 && (
                        <span className="text-xs font-normal text-slate-400">(apenas o mais recente é visível)</span>
                      )}
                    </p>
                    <span className="text-xs text-slate-400">
                      {canViewAllDocs ? `${contractFiles.length} ficheiro${contractFiles.length !== 1 ? 's' : ''}` : ''}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {(canViewAllDocs ? contractFiles : contractFiles.slice(0, 1)).map((f, idx) => {
                      const isCorrupt = !!f.is_corrupt;
                      return (
                      <li
                        key={f.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition ${isCorrupt ? 'bg-red-50 border-red-200' : idx === 0 ? 'bg-emerald-50/60 border-emerald-200 hover:border-emerald-300 cursor-pointer hover:shadow-sm' : 'bg-white border-slate-200 hover:border-slate-300 cursor-pointer hover:shadow-sm'}`}
                        onClick={() => { if (!isCorrupt) handleViewFile(f.id); }}
                        data-testid={`contract-doc-${idx}`}
                      >
                        {isCorrupt ? (
                          <AlertTriangle className="w-5 h-5 flex-shrink-0 text-red-600" />
                        ) : (
                          <FileText className={`w-5 h-5 flex-shrink-0 ${idx === 0 ? 'text-emerald-600' : 'text-slate-500'}`} />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-slate-800 truncate">{f.filename}</p>
                            {isCorrupt && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white">
                                CORROMPIDO
                              </span>
                            )}
                            {!isCorrupt && idx === 0 && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white" data-testid="badge-mais-recente">
                                MAIS RECENTE
                              </span>
                            )}
                            {f.via_ticket && (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                                via ticket
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {formatSize(f.size)}
                            {f.uploaded_by_name && f.uploaded_by_name !== '—' && <span> · enviado por <span className="font-medium">{f.uploaded_by_name}</span></span>}
                            {f.created_at && <span> · {formatDate(f.created_at)}</span>}
                          </p>
                          {isCorrupt && (
                            <p className="text-xs text-red-700 mt-1">
                              Ficheiro indisponível no storage. {canReplaceCorrupt ? 'Clique em "Reenviar" para substituir.' : 'Contacte um gestor para reenviar.'}
                            </p>
                          )}
                        </div>
                        {isCorrupt && canReplaceCorrupt ? (
                          <label className="cursor-pointer flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => handleReplaceCorrupt(f.id, e.target.files)}
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt,.webp"
                              data-testid={`replace-input-${f.id}`}
                            />
                            <Button type="button" variant="outline" size="sm" className="h-8 gap-1 border-red-300 text-red-700 hover:bg-red-100" asChild data-testid={`replace-file-${f.id}`}>
                              <span>
                                <Upload className="w-3.5 h-3.5" />
                                Reenviar
                              </span>
                            </Button>
                          </label>
                        ) : (
                          <Eye className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        )}
                      </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Reply form */}
              {ticket.status !== 'concluido' && (
                <div className="border-t border-slate-100 p-6">
                  <form onSubmit={handleSubmitReply} className="space-y-3" data-testid="reply-form">
                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Escreva a sua resposta..."
                      className="min-h-[100px] resize-none"
                      data-testid="reply-textarea"
                    />
                    {pendingFiles.length > 0 && (
                      <div className="space-y-2">
                        {pendingFiles.map((f, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                            <Paperclip className="w-4 h-4 text-amber-700 flex-shrink-0" />
                            <span className="flex-1 truncate text-slate-700">{f.name}</span>
                            <span className="text-xs text-slate-500">{formatSize(f.size)}</span>
                            <button
                              type="button"
                              onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`remove-attachment-${idx}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2D3E6F] transition-colors">
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const newFiles = Array.from(e.target.files || []);
                            if (newFiles.length) setPendingFiles(prev => [...prev, ...newFiles]);
                            e.target.value = '';
                          }}
                          accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
                          data-testid="attachment-input"
                        />
                        <Paperclip className="w-4 h-4" />
                        {pendingFiles.length > 0 ? `Adicionar mais (${pendingFiles.length} anexado${pendingFiles.length !== 1 ? 's' : ''})` : 'Anexar ficheiro(s)'}
                      </label>
                      <Button
                        type="submit"
                        disabled={sending}
                        className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2"
                        data-testid="send-reply-button"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Enviar
                      </Button>
                    </div>
                    {ticket.contract_id && pendingFiles.length > 0 && (
                      <p className="text-xs text-slate-500 bg-emerald-50 border border-emerald-200 p-2 rounded">
                        Os ficheiros serão automaticamente associados ao contrato <span className="font-medium">{ticket.contract_name || ''}</span>.
                      </p>
                    )}
                  </form>
                </div>
              )}
            </Card>
          </div>

          {/* Side column: meta */}
          <div className="space-y-6">
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Detalhes</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4 text-sm">
                <div>
                  <p className="text-xs text-slate-400 mb-1">Estado</p>
                  <StatusBadge status={ticket.status} />
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Prioridade</p>
                  <StatusBadge status={ticket.priority} />
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
                    <UserCog className="w-3 h-3" />
                    Atribuído a
                  </p>
                  {canManage ? (
                    <Select
                      value={ticket.assigned_to || ''}
                      onValueChange={handleAssigneeChange}
                      disabled={updatingAssignee || users.length === 0}
                    >
                      <SelectTrigger className="h-9" data-testid="assignee-select">
                        <SelectValue placeholder={ticket.assigned_to_name || 'Atribuir...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id} data-testid={`assignee-option-${u.id}`}>
                            {u.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium text-slate-700">{ticket.assigned_to_name || '-'}</p>
                  )}
                </div>
                {ticket.team_names && ticket.team_names.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Equipa</p>
                    <p className="font-medium text-slate-700">{ticket.team_names.join(', ')}</p>
                  </div>
                )}
                {ticket.agendamento && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Agendamento</p>
                    <p className="font-medium text-slate-700">{formatDate(ticket.agendamento)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-400 mb-1">Criado em</p>
                  <p className="font-medium text-slate-700">{formatDate(ticket.created_at)}</p>
                </div>
                {ticket.tipo === 'pedido_anulacao' && ticket.status !== 'concluido' && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                    ℹ️ Quando este ticket passar a "Concluído", o contrato será anulado automaticamente.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Audit timeline */}
            {audit.length > 0 && (
              <Card className="rounded-2xl border-slate-100" data-testid="audit-timeline-card">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <History className="w-4 h-4 text-slate-400" />
                    Histórico
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {audit.map(entry => (
                      <li key={entry.id} className="flex gap-3 text-xs" data-testid={`audit-${entry.id}`}>
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#2D3E6F] mt-1.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-700">
                            <span className="font-semibold">{entry.created_by_name || 'Sistema'}</span>{' '}
                            alterou <span className="font-medium text-slate-500">{
                              entry.field === 'status' ? 'estado'
                              : entry.field === 'assigned_to' ? 'atribuição'
                              : entry.field === 'priority' ? 'prioridade'
                              : entry.field
                            }</span>{' '}
                            de <span className="text-slate-500">{entry.old_value || '—'}</span>{' '}
                            <ArrowRight className="w-3 h-3 inline text-slate-400" />{' '}
                            <span className="font-semibold text-[#2D3E6F]">{entry.new_value || '—'}</span>
                          </p>
                          <p className="text-slate-400 mt-0.5">{formatDate(entry.created_at)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {ticket.contract_id && (
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Contrato Associado</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Cliente</p>
                    <p className="font-medium text-slate-800">{ticket.contract_name || '-'}</p>
                  </div>
                  {ticket.company_name && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Empresa</p>
                      <p className="font-medium text-slate-700">{ticket.company_name}</p>
                    </div>
                  )}
                  <Link
                    to={`/contracts/all`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/contracts/all`, { state: { highlightId: ticket.contract_id } });
                    }}
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#2D3E6F] hover:underline"
                    data-testid="link-to-contract"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Abrir contrato
                  </Link>
                  <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded mt-2">
                    Ficheiros anexados às respostas deste ticket são automaticamente associados ao contrato com a etiqueta <span className="font-semibold text-emerald-700">MAIS RECENTE</span>.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TicketDetail;
